<template>
  <form>
    <div class="form-group mt-3">
      <label for="password"><span v-show="isChange">New </span>Password:</label>
      <input type="password" id="password" class="form-control"
             @input="debouncePassword" ref="passwordInput" :class="'is-' + passwordClass"/>
    </div>

    <ul class="password-rules">
      <li
        v-for="rule in rules"
        :key="rule.code"
        :class="rule.textClass"
      >
        <i class="fas" :class="[rule.icon]"/>
        {{ rule.text }}
      </li>
    </ul>

    <div class="form-group mt-3">
      <label for="repeatPassword">Repeat Password:</label>
      <input type="password" id="repeatPassword" class="form-control" v-model="repeat"
             :class="'is-' + repeatClass"/>
      <div class="invalid-feedback" v-show="!isMatch && hasRepeat">Passwords don't match</div>
    </div>
  </form>

</template>

<script>
import _ from 'lodash'
import userPasswordService from './user-password-service'
import exceptionHandler from '@/common/exception-handler'
import exceptionMixin from '@/common/mixins/exception-mixin'

export default {
  name: 'user-password',
  mixins: [exceptionMixin],
  props: {
    userId: {},
    isChange: { default: false }
  },
  data () {
    return {
      password: '',
      repeat: '',
      validPassword: false,
      rules: [
        { code: 'PW_TOO_SHORT', icon: 'fa-times', text: 'Must be 7 characters minimum', textClass: 'text-danger' },
        { code: 'PW_MISSING_LETTERS', icon: 'fa-times', text: 'Must contain a letter', textClass: 'text-danger' },
        { code: 'PW_MISSING_NUMBERS', icon: 'fa-times', text: 'Must contain a number', textClass: 'text-danger' }
      ]
    }
  },
  computed: {
    isMatch: function () { return this.password === this.repeat },
    isValid: function () {
      return this.validPassword && this.isMatch
    },
    passwordClass: function () {
      if (this.password.length === 0) {
        return ''
      }

      return this.validPassword ? 'valid' : 'invalid'
    },
    repeatClass: function () {
      if (this.repeat.length === 0) {
        return ''
      }

      return this.isMatch ? 'valid' : 'invalid'
    },
    hasRepeat: function () { return this.repeat.length > 0 }
  },
  watch: {
    isValid: function () {
      this.$emit('valid-changed', this.isValid)
    }
  },
  methods: {
    debouncePassword: _.debounce(function (e) {
      this.password = e.target.value
      this.$emit('password-changed', this.password)
      this.validatePassword()
    }, 300),
    validatePassword () {
      this.validPassword = false

      userPasswordService.validatePassword(this.userId, this.password)
        .then(() => {
          this.cleanErrors()
          this.validPassword = true
        })
        .catch(errorList => {
          if (exceptionHandler.isUserError(errorList)) {
            this.validPassword = false
            this.cleanErrors()
            errorList.forEach(error => {
              this.setRule(error.code, false)
            })
          } else {
            this.handleError(errorList, 'Unable to validate password.')
          }
        })
    },
    cleanErrors () {
      this.rules.forEach(rule => this.setRule(rule.code, true))
    },
    setRule (code, valid) {
      if (code === 'PW_PREVIOUSLY_USED') {
        if (valid) {
          this.rules.splice(this.rules.findIndex(next => next.code === code), 1)
        } else {
          this.rules.push({
            code: 'PW_PREVIOUSLY_USED',
            icon: 'fa-times',
            text: 'Must not be one of your last 4 passwords',
            textClass: 'text-danger'
          })
        }
        return
      }

      let rule = this.rules.find(next => next.code === code)

      if (valid) {
        rule.icon = 'fa-check'
        rule.textClass = 'text-success'
      } else {
        rule.icon = 'fa-times'
        rule.textClass = 'text-danger'
      }
    },
    reset () {
      this.password = ''
      this.$refs.passwordInput.value = ''
      this.repeat = ''
      this.rules.forEach(rule => this.setRule(rule.code, false))
    }
  }
}
</script>

<style scoped="">
  ul.password-rules {
    list-style: outside none;
    padding-left: 15px
  }
</style>
